import { cloneDeep } from "lodash";
import { Edge, Node, MarkerType } from "reactflow";
import { ActionTypeBackend } from "./macroNode.types";

export enum MacroStatus {
    LIVE = "live",
    CREATED = "created",
    ARCHIVED = "archived"
}

/**
 * MacroNodeManager
 * A specialized Node Manager for the Macros builder.
 * Unlike the main builder which relies on Redux storeState.flow,
 * this manager strictly operates on React Flow nodeData arrays
 * and expects clean, custom backendConfig shapes (e.g. multiAction).
 */
export class MacroNodeManager {
    /**
     * Identifies the edge connection source vs target points
     */
    getEdgeConnections(params: any) {
        const sourceStringArray = params?.sourceHandle?.split("?");
        let sourceId = "";
        if (sourceStringArray && sourceStringArray.length > 0) {
            sourceId = sourceStringArray[0];
        }
        const toNodeId = params.target?.split("?")[0] || params.target;
        return {
            fromNodeId: params.source,
            sourceHandler: sourceId,
            toNodeId: toNodeId,
        };
    }

    /**
     * Mutates the source node's backend config to store the edge connection
     * (e.g. setting 'next: toNodeId' or 'fallback: toNodeId')
     * Expects nodes to have the new clean Macro format
     */
    handleEdgeConnections(editableNode: any, edgeData: any, oldEdge: any = null) {
        let error = "";
        try {
            editableNode = cloneDeep(editableNode);

            // We handle the new MultiAction style format, Condition format, etc
            switch (editableNode?.type) {
                case ActionTypeBackend.TRIGGER: {
                    // For macro_starting_point, edgeData.sourceHandler is the channel (e.g. "whatsapp", "email")
                    if (edgeData?.sourceHandler && edgeData.sourceHandler !== "source" && edgeData.sourceHandler !== "target" && edgeData.sourceHandler !== "fallback") {
                        if (!editableNode.trigger) editableNode.trigger = { conditions: [{ operator_values: {} }] };
                        if (!editableNode.trigger.conditions) editableNode.trigger.conditions = [{ operator_values: {} }];
                        if (!editableNode.trigger.conditions[0]) editableNode.trigger.conditions[0] = { operator_values: {} };
                        if (!editableNode.trigger.conditions[0].operator_values) editableNode.trigger.conditions[0].operator_values = {};

                        editableNode.trigger.conditions[0].operator_values[edgeData.sourceHandler] = {
                            key: edgeData.sourceHandler,
                            value: edgeData?.toNodeId
                        };
                    } else if (edgeData?.sourceHandler === "fallback") {
                        editableNode.fallback_node = edgeData?.toNodeId;
                    } else {
                        // standard trigger connection
                        editableNode.next = edgeData?.toNodeId;
                    }
                    break;
                }

                case ActionTypeBackend.MULTI_ACTION:
                case ActionTypeBackend.START_A_FLOW:
                case ActionTypeBackend.MESSAGE: {
                    if (edgeData?.sourceHandler === "fallback") {
                        editableNode.fallback_node = edgeData?.toNodeId;
                    } else {
                        // Next step connection
                        editableNode.next = edgeData?.toNodeId;
                    }
                    break;
                }

                default: {
                    // Default fallback: attach to 'next'
                    if (edgeData?.sourceHandler === "fallback") {
                        editableNode.fallback_node = edgeData?.toNodeId;
                    } else {
                        editableNode.next = edgeData?.toNodeId;
                    }
                    break;
                }
            }
        } catch (err: any) {
            error = err?.message || "error";
        } finally {
            return { editableNode, error };
        }
    }

    /**
     * Iterates edges and delete the reference in source node
     */
    handleNodesDeletion(
        nodes: any[],
        deleteNodeIds: string[],
        toDeleteEdges: Edge[]
    ) {
        try {
            const newNodes = cloneDeep(nodes).filter(
                (node: any) => !deleteNodeIds.includes(node.id)
            );

            for (const edge of toDeleteEdges) {
                for (const deleteNodeId of deleteNodeIds) {
                    const sourceNodeId = edge.source === deleteNodeId ? edge.target : edge.source;
                    const nodeToUpdate = newNodes.find((n: any) => n.id === sourceNodeId);

                    if (nodeToUpdate) {
                        const { nodeData, error } = this.removeReferences(
                            nodeToUpdate.data.nodeData.backendConfig,
                            deleteNodeId,
                            edge.sourceHandle || undefined
                        );
                        if (!error) {
                            nodeToUpdate.data.nodeData.backendConfig = nodeData;
                        }
                    }
                }
            }
            return { newNodes, error: "" };
        } catch (error: any) {
            return { newNodes: nodes, error: error?.message };
        }
    }

    handleEdgesDeletion(edges: Edge[], toDeleteEdge: any, nodes: any[]) {
        try {
            const newNodes = cloneDeep(nodes);
            const sourceNodeUrl = newNodes.find((n: any) => n.id === toDeleteEdge.source);

            if (sourceNodeUrl) {
                const { nodeData, error } = this.removeReferences(
                    sourceNodeUrl.data.nodeData.backendConfig,
                    toDeleteEdge.target,
                    toDeleteEdge.sourceHandleId
                );
                if (!error) {
                    sourceNodeUrl.data.nodeData.backendConfig = nodeData;
                }
            }

            const newEdges = edges.filter((edge: any) => toDeleteEdge.id !== edge.id) || edges;
            return { newNodes, newEdges, error: "" };
        } catch (error: any) {
            return { newNodes: nodes, newEdges: edges, error: error?.message };
        }
    }

    removeReferences(
        nodeConfig: any,
        deleteId: string,
        sourceId?: string,
        forceEmpty: boolean = false
    ) {
        let error = "";
        try {
            if (!nodeConfig) return { nodeData: nodeConfig, error: "" };

            // General fallback for our cleaner Macro shapes
            if (forceEmpty || nodeConfig.next === deleteId) {
                nodeConfig.next = "";
            }
            if (forceEmpty || nodeConfig.fallback_node === deleteId) {
                nodeConfig.fallback_node = "";
            }
            if (nodeConfig.branches) {
                for (const key of Object.keys(nodeConfig.branches)) {
                    if (forceEmpty || (nodeConfig.branches[key] === deleteId && key === sourceId)) {
                        delete nodeConfig.branches[key];
                    }
                }
            }

            if (nodeConfig.trigger?.conditions?.[0]?.operator_values) {
                const opVals = nodeConfig.trigger.conditions[0].operator_values;
                for (const k of Object.keys(opVals)) {
                    if (forceEmpty || (opVals[k]?.value === deleteId && k === sourceId)) {
                        opVals[k].value = "";
                    }
                }
            }

        } catch (err: any) {
            error = err?.message || "error";
        } finally {
            return { nodeData: nodeConfig, error };
        }
    }

    /**
     * Reads the backend configuration and dynamically generates 
     * the React Flow Edges array. This removes the need to store 
     * physical UI edges in Firebase.
     */
    rebuildEdgesFromConfig(backendNodes: { [key: string]: any }): Edge[] {
        const edges: Edge[] = [];
        if (!backendNodes) return edges;

        Object.entries(backendNodes).forEach(([sourceId, nodeConfig]) => {
            if (!nodeConfig) return;

            const addEdge = (targetId: string, sourceHandle: string) => {
                if (!targetId || targetId.trim() === "") return;
                edges.push({
                    id: `reactflow__edge-${sourceId}${sourceHandle}-${targetId}target`,
                    source: sourceId,
                    target: targetId,
                    sourceHandle: sourceHandle,
                    targetHandle: "target",
                    type: "CustomEdge",
                    animated: false,
                    style: {
                        stroke: "#a1a1a5",
                        strokeWidth: "2",
                        fill: "none",
                    },
                    interactionWidth: 40,
                    markerEnd: {
                        type: MarkerType.ArrowClosed,
                        strokeWidth: 5,
                    },
                });
            };

            // 1. Check Standard Next Step
            if (nodeConfig.next) {
                addEdge(nodeConfig.next, "source");
            }

            // 2. Check Fallback
            if (nodeConfig.fallback_node) {
                addEdge(nodeConfig.fallback_node, "fallback");
            }

            // 3. Check Branches (Condition nodes)
            if (nodeConfig.branches) {
                Object.entries(nodeConfig.branches).forEach(([branchKey, targetId]) => {
                    addEdge(targetId as string, branchKey);
                });
            }

            // 4. Check Trigger Channels (Starting Point)
            if (nodeConfig.trigger?.conditions?.[0]?.operator_values) {
                const opVals = nodeConfig.trigger.conditions[0].operator_values;
                Object.entries(opVals).forEach(([channelKey, data]: [string, any]) => {
                    if (data?.value) {
                        addEdge(data.value, channelKey);
                    }
                });
            }
        });

        return edges;
    }


}

export const macroNodeManager = new MacroNodeManager();
